import { useMemo, useState } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import { LayoutGrid, Pencil, Plus, RotateCcw } from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import { useI18n } from '../contexts/I18nContext';
import { useConfirm } from '../components/ui/ConfirmProvider';
import {
  useDashboardLayout,
  useResetDashboardLayout,
  useSaveDashboardLayout,
} from '../hooks/useDashboardLayout';
import { DashboardGrid } from '../components/dashboard/DashboardGrid';
import { WidgetCatalog } from '../components/dashboard/WidgetCatalog';
import {
  getWidgetDef,
  sanitizeLayout,
  type DashboardLayoutV1,
  type WidgetDef,
} from '../components/dashboard/widgets/widgetRegistry';

/**
 * COO Executive Command Center (CLE-256), now configurable: the page renders a
 * per-user widget layout (12-column grid, preset widths) with an edit mode —
 * add from a catalog, drag & drop to reorder, cycle widths, remove. The saved
 * layout lives server-side (`/api/dashboard/layout`); no saved row = the
 * default layout, which reproduces the original fixed page.
 */
export function DashboardPage() {
  const { t } = useI18n();
  const { can } = usePermissions();
  const confirm = useConfirm();

  const saved = useDashboardLayout();
  const saveLayout = useSaveDashboardLayout();
  const resetLayout = useResetDashboardLayout();

  // null = view mode; non-null = the layout being edited.
  const [draft, setDraft] = useState<DashboardLayoutV1 | null>(null);
  const [catalogOpen, setCatalogOpen] = useState(false);

  // Saved (or default) layout, RBAC-filtered and clamped to valid presets.
  const effective = useMemo(() => sanitizeLayout(saved.data ?? null, can), [saved.data, can]);

  const editing = draft !== null;
  const layout = draft ?? effective;

  // --- Draft operations (pure updates on the draft state) -------------------

  const startEditing = () => setDraft(structuredClone(effective));

  const reorder = (fromIndex: number, toIndex: number) =>
    setDraft((d) => (d === null ? d : { ...d, widgets: arrayMove(d.widgets, fromIndex, toIndex) }));

  const cycleWidth = (id: string) =>
    setDraft((d) => {
      if (d === null) return d;
      const def = getWidgetDef(id);
      if (!def) return d;
      return {
        ...d,
        widgets: d.widgets.map((w) => {
          if (w.id !== id) return w;
          const idx = def.allowedWidths.indexOf(w.w);
          const next = def.allowedWidths[(idx + 1) % def.allowedWidths.length];
          return { ...w, w: next };
        }),
      };
    });

  const removeWidget = (id: string) =>
    setDraft((d) => (d === null ? d : { ...d, widgets: d.widgets.filter((w) => w.id !== id) }));

  // Catalog add/remove works at any moment: in edit mode it touches the draft;
  // in view mode each change is persisted immediately.
  const addWidget = (def: WidgetDef) => {
    if (editing) {
      setDraft((d) =>
        d === null || d.widgets.some((w) => w.id === def.id)
          ? d
          : { ...d, widgets: [...d.widgets, { id: def.id, w: def.defaultWidth }] },
      );
      return;
    }
    if (effective.widgets.some((w) => w.id === def.id)) return;
    saveLayout.mutate({
      version: 1,
      widgets: [...effective.widgets, { id: def.id, w: def.defaultWidth }],
    });
  };

  const removeFromCatalog = (id: string) => {
    if (editing) {
      removeWidget(id);
      return;
    }
    saveLayout.mutate({
      version: 1,
      widgets: effective.widgets.filter((w) => w.id !== id),
    });
  };

  // --- Toolbar actions -------------------------------------------------------

  const handleSave = () => {
    if (draft === null) return;
    saveLayout.mutate(draft, {
      onSuccess: () => {
        setDraft(null);
        setCatalogOpen(false);
      },
    });
  };

  const handleCancel = () => {
    setDraft(null);
    setCatalogOpen(false);
    saveLayout.reset();
  };

  const handleReset = async () => {
    if (!(await confirm({ message: t('dashboard.edit.resetConfirm') }))) return;
    resetLayout.mutate(undefined, {
      onSuccess: () => {
        setDraft(null);
        setCatalogOpen(false);
      },
    });
  };

  const usedIds = useMemo(() => new Set(layout.widgets.map((w) => w.id)), [layout]);

  return (
    <div className="w-full space-y-6 p-4 sm:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">
            {t('dashboard.title')}
          </h1>
          <p className="text-sm text-muted-foreground">{t('dashboard.subtitle')}</p>
        </div>

        {/* Phones: the toolbar as a grid of equal halves (2×2 while editing),
            not a ragged wrap under the title. */}
        {editing ? (
          <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:items-center">
            <button
              type="button"
              onClick={() => setCatalogOpen(true)}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground transition hover:bg-gray-200"
            >
              <Plus className="h-4 w-4" aria-hidden />
              {t('dashboard.edit.addWidget')}
            </button>
            <button
              type="button"
              onClick={handleReset}
              disabled={resetLayout.isPending}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-muted-foreground transition hover:bg-gray-200 hover:text-foreground disabled:opacity-50"
            >
              <RotateCcw className="h-4 w-4" aria-hidden />
              <span className="sm:hidden">{t('dashboard.edit.resetShort')}</span>
              <span className="hidden sm:inline">{t('dashboard.edit.reset')}</span>
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="inline-flex items-center justify-center rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-muted-foreground transition hover:bg-gray-200 hover:text-foreground"
            >
              {t('dashboard.edit.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saveLayout.isPending}
              className="inline-flex items-center justify-center rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-50"
            >
              {t('dashboard.edit.save')}
            </button>
          </div>
        ) : (
          <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:items-center">
            <button
              type="button"
              onClick={() => setCatalogOpen(true)}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-muted-foreground transition hover:bg-gray-200 hover:text-foreground"
            >
              <LayoutGrid className="h-4 w-4" aria-hidden />
              {t('dashboard.edit.widgets')}
            </button>
            <button
              type="button"
              onClick={startEditing}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-muted-foreground transition hover:bg-gray-200 hover:text-foreground"
            >
              <Pencil className="h-4 w-4" aria-hidden />
              <span className="sm:hidden">{t('dashboard.edit.enterShort')}</span>
              <span className="hidden sm:inline">{t('dashboard.edit.enter')}</span>
            </button>
          </div>
        )}
      </header>

      {saveLayout.isError && (
        <p className="text-sm text-red-400">{t('dashboard.edit.saveError')}</p>
      )}

      {editing ? (
        <div className="rounded-2xl border border-dashed border-border p-3 sm:p-4">
          {layout.widgets.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-16 text-center">
              <LayoutGrid className="h-8 w-8 text-muted-foreground" aria-hidden />
              <p className="text-sm text-muted-foreground">{t('dashboard.edit.empty')}</p>
              <button
                type="button"
                onClick={() => setCatalogOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground transition hover:bg-gray-200"
              >
                <Plus className="h-4 w-4" aria-hidden />
                {t('dashboard.edit.addWidget')}
              </button>
            </div>
          ) : (
            <DashboardGrid
              widgets={layout.widgets}
              editing
              onReorder={reorder}
              onCycleWidth={cycleWidth}
              onRemove={removeWidget}
            />
          )}
        </div>
      ) : (
        <DashboardGrid widgets={layout.widgets} editing={false} />
      )}

      {catalogOpen && (
        <WidgetCatalog
          usedIds={usedIds}
          can={can}
          onAdd={addWidget}
          onRemove={removeFromCatalog}
          onClose={() => setCatalogOpen(false)}
        />
      )}
    </div>
  );
}
